import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 30000,
});

// Cold-start resilience: a free-tier backend can be asleep, and the Netlify /api
// proxy times out (~26s) or the backend returns 502/503/504 while waking. Retry a
// few times with a short delay so the request succeeds once the backend is warm.
const RETRY_STATUSES = [502, 503, 504];
const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 3000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const isTimeout = error.code === "ECONNABORTED";
    const isNetwork = !error.response && error.message === "Network Error";
    const retriable = isTimeout || isNetwork || RETRY_STATUSES.includes(status);

    config.__retryCount = config.__retryCount || 0;
    if (retriable && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;
      await sleep(RETRY_DELAY_MS);
      return api(config);
    }
    return Promise.reject(error);
  }
);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (d == null) {
    if (e?.code === "ECONNABORTED" || (!e?.response && e?.message === "Network Error")) {
      return "The server is waking up — please try again in a moment.";
    }
    return e?.message || "Something went wrong";
  }
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && x.msg) || JSON.stringify(x)).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}

export default api;
